import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Wallet, Landmark, ArrowUpRight, ArrowDownLeft,
  Users, FileText, HelpCircle, CalendarClock, ClipboardCheck,
  Send, Bot, CheckCheck, Coins, CalendarDays, RefreshCw,
  AlertTriangle, ChevronRight, Info, Settings as SettingsIcon,
  PlayCircle, Loader2,
} from "lucide-react";
import {
  AreaChart, Area, LineChart, Line, PieChart, Pie, Cell,
  BarChart, Bar, LabelList,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useAppearance } from "@/theme/ThemeProvider";
import { useDashboardActions } from "@/context/DashboardActionsContext";
import CumulativeNetArea from "@/components/charts/CumulativeNetArea";
import RadialProgressRing from "@/components/charts/RadialProgressRing";
import BankStatementRows, { SparklineTrend } from "@/components/charts/BankStatementRows";
import IncomeComboChart from "@/components/charts/IncomeComboChart";
import BudgetSettingsModal from "@/components/BudgetSettingsModal";

/* Phase 39 (2026-07-28) — Option 4 "Modern SaaS Cockpit" Dashboard.
 * Preserves every existing drill-through route + RBAC role-visibility rules.
 * Adds: welcome ribbon, 3 hero KPI cards with sparklines, cash flow area chart,
 * expense mix doughnut, secondary KPI grid, operational alerts, activity split. */

const NAVY = "#0F1E3D";
const BLUE = "#1F4FA3";
const RED  = "#E63946";
const GREEN = "#0E9F6E";
const AMBER = "#D97706";

function fmtMoney(n) {
  const v = Number(n || 0);
  return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtCompact(n) {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
}

function buildWelcome(user, stats) {
  if (!user) return { greeting: "Welcome", message: "Loading…" };
  const first = (user.full_name || user.email).split(" ")[0];
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const s = stats || {};
  switch (user.role) {
    case "system_admin":
      return { greeting: `Welcome back, ${first}.`, message: `${today} — ${s.reports_pending_approval || 0} approvals waiting, ${s.compliance_deadlines || 0} compliance items due in 30 days.` };
    case "somturk_data_entry":
      return { greeting: `Welcome, ${first}.`, message: `${today} — ${s.submitted || 0} submitted, ${s.clarification_needed || 0} clarification requests, ${s.missing_documents || 0} missing-document items.` };
    case "somturk_observer":
      return { greeting: `Welcome, ${first}.`, message: `${today} — ${s.reports_pending_approval || 0} reports pending review · ${s.compliance_deadlines || 0} compliance items due in 30 days.` };
    default:
      return { greeting: `Welcome, ${first}.`, message: today };
  }
}

/* ------- small components ------- */

function Sparkline({ series, color = BLUE, positive = true }) {
  if (!series || series.length === 0) return <div className="h-14" />;
  const data = series.map((p) => ({ x: p.date, y: p.value }));
  return (
    <ResponsiveContainer width="100%" height={56}>
      <LineChart data={data} margin={{ top: 4, right: 0, bottom: 4, left: 0 }}>
        <Line
          type="monotone"
          dataKey="y"
          stroke={positive ? color : "#94A3B8"}
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function MomChip({ deltaPct, currentAbs, prevAbs, sameSign }) {
  // Guard: hide the chip when either side is < $100 or when signs disagree,
  // or when the computed pct is nonsensical (> 999 or NaN).
  const hide =
    !Number.isFinite(deltaPct) ||
    currentAbs < 100 || prevAbs < 100 ||
    !sameSign || Math.abs(deltaPct) >= 999;
  if (hide) {
    return (
      <span
        title="Insufficient history for a meaningful comparison"
        className="inline-block mt-3 rounded-full px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-500"
        data-testid="hero-mom-hidden"
      >
        —
      </span>
    );
  }
  const chipBg = deltaPct >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700";
  const shown = Math.max(-999, Math.min(999, deltaPct));
  return (
    <span
      className={`inline-block mt-3 rounded-full px-2 py-0.5 text-xs font-medium ${chipBg}`}
      data-testid="hero-mom-chip"
    >
      {`${shown >= 0 ? "+" : ""}${shown.toFixed(1)}% MoM`}
    </span>
  );
}

/** Phase 45 (2026-07-28) - WoW pill for the Cash Position hero card. */
function WowChip({ hide, deltaPct }) {
  if (hide) {
    return (
      <span
        title="Insufficient history for a WoW comparison"
        className="inline-block mt-3 rounded-full px-2 py-0.5 text-xs font-medium"
        style={{ backgroundColor: "var(--bg-hover)", color: "var(--text-muted)" }}
        data-testid="hero-wow-hidden"
      >
        —
      </span>
    );
  }
  const positive = deltaPct >= 0;
  const arrow = positive ? "^" : "v";
  const shown = Math.max(-999, Math.min(999, deltaPct));
  return (
    <span
      className="inline-block mt-3 rounded-full px-2 py-0.5 text-xs font-medium"
      style={{
        backgroundColor: positive ? "var(--success-pale)" : "var(--danger-pale)",
        color: positive ? "var(--success)" : "var(--danger)",
      }}
      data-testid="hero-wow-chip"
    >
      {`${arrow} ${positive ? "+" : ""}${shown.toFixed(1)}% WoW`}
    </span>
  );
}

function CashPositionPopover({ breakdown, onClose }) {
  if (!breakdown) return null;
  const banks = (breakdown.accounts || []).filter((a) => a.kind === "bank");
  const fmt = (v) =>
    `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div
      className="absolute right-0 top-full mt-2 w-[380px] rounded-xl bg-white border border-slate-200 shadow-lg p-4 z-30"
      onClick={(e) => e.stopPropagation()}
      data-testid="cash-position-popover"
    >
      <div className="flex items-center justify-between">
        <p className="text-[11px] tracking-[0.22em] uppercase text-slate-500 font-medium">
          Cash Position — active banks
        </p>
        <button
          onClick={onClose}
          className="text-xs text-slate-500 hover:text-slate-800"
          aria-label="Close"
          data-testid="cash-position-popover-close"
        >
          ×
        </button>
      </div>
      <p className="mt-2 text-[11px] text-slate-500 bg-slate-50 rounded p-2 leading-snug"
         data-testid="cash-position-popover-tooltip">
        Sum of your live bank account balances. Excludes Cash on Hand and Petty Cash.
      </p>
      <div className="mt-3 max-h-[280px] overflow-y-auto" data-testid="cash-position-popover-banks">
        {banks.length === 0 ? (
          <p className="text-xs text-slate-400 italic py-2">No active bank accounts.</p>
        ) : banks
          .sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance))
          .map((r, i) => (
            <div key={`b-${i}`} className="flex items-center justify-between py-1 text-sm">
              <span className="text-slate-700 truncate max-w-[260px]">
                <span className="text-slate-400 tabular-nums mr-2">{r.code || "—"}</span>
                {r.name}
              </span>
              <span className={`tabular-nums font-medium ${Number(r.balance) < 0 ? "text-red-600" : "text-slate-800"}`}>
                {fmt(r.balance)}
              </span>
            </div>
          ))}
      </div>
      <div className="mt-3 flex justify-between text-sm font-semibold text-slate-900 border-t border-slate-200 pt-2">
        <span>Total Cash Position</span>
        <span className={`tabular-nums ${Number(breakdown.total) < 0 ? "text-red-600" : ""}`}>
          {fmt(breakdown.total)}
        </span>
      </div>
    </div>
  );
}

function HeroCard({
  label, value, deltaPct, series, sparkColor, positive, to, testid,
  momContext, cashBreakdown, integrityDriftAbs, companion,
  rightSlot, chipOverride, asOf, subtitle,
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const numeric = Number(value?.toString().replace(/[^0-9.-]/g, "") || 0);
  const isNegative = numeric < 0;
  const companionSum = companion ? Number(companion.sum || 0) : 0;
  const showCompanionChip = companion && Math.abs(companionSum) > 0.005;

  const inner = (
    <div
      className="rounded-2xl bg-white border border-slate-200 p-6 h-full flex flex-col relative transition-all"
      style={{ boxShadow: "0 1px 2px rgba(15,30,61,0.04), 0 10px 20px -16px rgba(15,30,61,0.15)" }}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-[11px] tracking-[0.22em] text-slate-500 uppercase font-medium">
              {label}
            </p>
            {cashBreakdown && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPopoverOpen((v) => !v); }}
                className="text-slate-400 hover:text-slate-700 leading-none"
                aria-label="Show contributing accounts"
                data-testid="cash-position-info-btn"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            )}
            {integrityDriftAbs > 0.01 && (
              <span
                title={`Data integrity mismatch — API total drifts from sum by $${integrityDriftAbs.toFixed(2)}. Auto-Heal will reconcile.`}
                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold"
                data-testid="hero-integrity-warning"
              >
                !
              </span>
            )}
          </div>
          <p className={`mt-2 text-[32px] font-semibold tabular-nums leading-none ${isNegative ? "text-red-600" : "text-slate-900"}`}>
            {value}
          </p>
          {subtitle && (
            <p
              className="mt-1 text-[11px]"
              style={{ color: "var(--text-muted)" }}
              data-testid={`${testid}-subtitle`}
            >
              {subtitle}
            </p>
          )}
          {chipOverride ? (
            chipOverride
          ) : (
            <MomChip
              deltaPct={deltaPct}
              currentAbs={momContext?.currentAbs ?? Math.abs(numeric)}
              prevAbs={momContext?.prevAbs ?? 999999}
              sameSign={momContext?.sameSign ?? true}
            />
          )}
          {showCompanionChip && (
            <Link
              to="/reports"
              onClick={(e) => e.stopPropagation()}
              className="mt-2 inline-flex items-center gap-1 rounded-full bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2.5 py-0.5 text-[11px] text-slate-600 transition-colors"
              data-testid="cash-position-companion-chip"
              title="Cash on Hand + Petty Cash — informational only, NOT part of Cash Position"
            >
              <span className="text-slate-500">Cash on Hand &amp; Petty Cash:</span>
              <span className={`tabular-nums font-medium ${companionSum < 0 ? "text-red-600" : "text-slate-800"}`}>
                {`${companionSum < 0 ? "-" : ""}$${Math.abs(companionSum).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </span>
              <ChevronRight className="h-3 w-3 text-slate-400" />
            </Link>
          )}
        </div>
        {rightSlot ? (
          <div className="shrink-0 flex items-start">
            {rightSlot}
          </div>
        ) : (
          <div className="w-[110px] h-14 shrink-0">
            <Sparkline series={series} color={sparkColor} positive={positive} />
          </div>
        )}
      </div>
      {popoverOpen && (
        <CashPositionPopover breakdown={cashBreakdown} onClose={() => setPopoverOpen(false)} />
      )}
      {/* Phase 2 (2026-08-09) — "As of" timestamp for widgets with a
          time-window (e.g. Total Expenses (MTD)). */}
      {asOf && (
        <p
          className="mt-3 text-[10px]"
          style={{ color: "var(--text-muted)" }}
          data-testid={`${testid}-as-of`}
        >
          As of {new Date(asOf).toLocaleString()}
        </p>
      )}
    </div>
  );
  if (popoverOpen || !to) return inner;
  return <Link to={to} className="block h-full">{inner}</Link>;
}

/**
 * Phase 47 (2026-07-29) - Cash Position hero card - Bank Statement Rows style.
 * Custom layout (not the generic HeroCard) so we can stack:
 *   Row 1: label + info icon + WoW pill
 *   Row 2: big value + 30-day sparkline
 *   Row 3: bank rows (BankStatementRows)
 *   Row 4: red warning strip (companion) - full width, shown only when sum < 0
 * The whole card remains clickable and navigates to /bank; buttons inside
 * (info popover, warning pill) stop propagation.
 */
function CashPositionHeroCard({
  value, cashBreakdown, integrityDriftAbs, companion, chipOverride,
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const navigate = useNavigate();
  const numeric = Number(value?.toString().replace(/[^0-9.-]/g, "") || 0);
  const isNegative = numeric < 0;
  const companionSum = companion ? Number(companion.sum || 0) : 0;
  // Phase 47 spec: warning strip visible only if the companion sum is NEGATIVE.
  const showWarning = companion && companionSum < -0.005;

  const goToBank = (e) => {
    if (popoverOpen) return;
    if (e.target.closest(
        "[data-testid='cash-position-info-btn'], " +
        "[data-testid='cash-position-warning-pill'], " +
        "[data-testid='cash-sparkline']"
    )) return;
    navigate("/bank");
  };

  return (
    <div
      className="rounded-2xl bg-white border border-slate-200 p-6 h-full flex flex-col relative transition-all cursor-pointer"
      style={{ boxShadow: "0 1px 2px rgba(15,30,61,0.04), 0 10px 20px -16px rgba(15,30,61,0.15)" }}
      data-testid="hero-hero-cash"
      onClick={goToBank}
    >
      {/* Row 1 - Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="text-[11px] tracking-[0.22em] text-slate-500 uppercase font-medium">
            Cash Position
          </p>
          {cashBreakdown && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPopoverOpen((v) => !v); }}
              className="text-slate-400 hover:text-slate-700 leading-none"
              aria-label="Show contributing accounts"
              data-testid="cash-position-info-btn"
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          )}
          {integrityDriftAbs > 0.01 && (
            <span
              title={`Data integrity mismatch - API total drifts from sum by $${integrityDriftAbs.toFixed(2)}. Auto-Heal will reconcile.`}
              className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold"
              data-testid="hero-integrity-warning"
            >
              !
            </span>
          )}
        </div>
        {chipOverride && (
          <div className="shrink-0">
            {chipOverride}
          </div>
        )}
      </div>

      {/* Row 2 - Hero line: big value on left, sparkline on right */}
      <div className="mt-2 flex items-end justify-between gap-3">
        <p
          className={`text-[28px] font-semibold tabular-nums leading-none ${isNegative ? "text-red-600" : "text-slate-900"}`}
          data-testid="cash-position-headline"
        >
          {value}
        </p>
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <SparklineTrend series={cashBreakdown?.sparkline || []} />
        </div>
      </div>

      {/* Row 3 - Bank statement rows */}
      <div className="mt-4">
        <BankStatementRows accounts={cashBreakdown?.accounts || []} />
      </div>

      {/* Row 4 - Companion warning strip (only when sum is negative) */}
      {showWarning && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Phase 47 - Spec asked for /reports/trial-balance?filter=cash but
            // that route doesn't exist. Trial Balance lives at /reports with
            // reportId=trial_balance selected.
            navigate("/reports?report=trial_balance&filter=cash");
          }}
          className="mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs w-fit hover:opacity-80 transition-opacity"
          style={{
            backgroundColor: "var(--danger-pale, #FEE2E2)",
            color: "var(--danger, #B10E1E)",
            border: "1px solid var(--danger-pale, #FEE2E2)",
          }}
          data-testid="cash-position-warning-pill"
          title="Cash on Hand + Petty Cash - informational only, NOT part of Cash Position"
        >
          <AlertTriangle className="h-3 w-3" />
          <span>Cash on Hand &amp; Petty Cash:</span>
          <span className="tabular-nums font-medium">
            {`-$${Math.abs(companionSum).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </span>
          <ChevronRight className="h-3 w-3" />
        </button>
      )}

      {popoverOpen && (
        <CashPositionPopover breakdown={cashBreakdown} onClose={() => setPopoverOpen(false)} />
      )}
    </div>
  );
}

/**
 * Phase 48 (2026-07-29) - Total Income hero card - 12-Month Combo Chart style.
 * Layout:
 *   Row 1: label + YoY chip
 *   Row 2: THIS MONTH big value + 12M TOTAL muted number
 *   Row 3: 12-month combo chart (bars + rolling-avg line)
 * Whole card clickable -> /reports?report=income_statement.
 */
function TotalIncomeHeroCard({ income12m, stats }) {
  const navigate = useNavigate();

  const currentIncome = Number(income12m?.current_month?.income || 0);
  // Phase 2 (2026-08-09) — the 12M total displayed on the hero card MUST
  // equal the Income Statement's total_income for the same 12-month
  // window. `/dashboard/stats.total_income` is now the authoritative
  // number (rewritten in accounting.py's dashboard_stats). Fall back to
  // the legacy `income12m.twelve_month_total` only if stats hasn't
  // loaded yet — never let the card show a diverging figure once both
  // endpoints have responded.
  const statsTotal = stats?.total_income;
  const twelveTotal = Number(
    statsTotal != null ? statsTotal : (income12m?.twelve_month_total || 0),
  );
  const series = income12m?.series || [];
  const currentMonthISO = income12m?.current_month?.month || null;
  const yoy = income12m?.yoy || null;
  const asOf = stats?.as_of || null;

  // Empty-state: every income entry is <= 0.005 -> flat placeholder chart.
  const isEmpty = series.length > 0 && series.every(
    (r) => Math.abs(Number(r.income || 0)) < 0.005,
  );

  const goToReport = (e) => {
    // Don't intercept clicks on the tooltip or the YoY chip.
    if (e.target.closest("[data-testid='income-combo-tooltip']")) return;
    navigate("/reports?report=income_statement");
  };

  return (
    <div
      className="rounded-2xl bg-white border border-slate-200 p-6 h-full flex flex-col relative transition-all cursor-pointer"
      style={{ boxShadow: "0 1px 2px rgba(15,30,61,0.04), 0 10px 20px -16px rgba(15,30,61,0.15)" }}
      data-testid="hero-hero-income"
      onClick={goToReport}
    >
      {/* Row 1 - Header */}
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] tracking-[0.24em] uppercase font-medium"
           style={{ color: "var(--text-secondary)" }}>
          Total Income (12M)
        </p>
        <YoyChip yoy={yoy} />
      </div>

      {/* Row 2 - Big number strip */}
      <div className="mt-2 flex items-end justify-between gap-3">
        <div>
          <p className="text-[9px] tracking-[0.20em] uppercase font-medium"
             style={{ color: "var(--text-muted)" }}>
            This Month
          </p>
          <p
            className="mt-0.5 text-[26px] font-semibold tabular-nums leading-none"
            style={{ color: currentIncome < 0 ? "var(--danger)" : "var(--text-primary)" }}
            data-testid="income-headline"
          >
            {fmtMoney(currentIncome)}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[9px] tracking-[0.20em] uppercase font-medium"
             style={{ color: "var(--text-muted)" }}>
            12M Total
          </p>
          <p className="mt-0.5 text-sm tabular-nums font-medium"
             style={{ color: "var(--text-muted)" }}
             data-testid="income-12m-total">
            {fmtMoney(twelveTotal)}
          </p>
        </div>
      </div>

      {/* Row 3 - Combo chart. */}
      <div className="mt-4 flex-1 min-h-[140px]">
        <IncomeComboChart
          series={series}
          currentMonthISO={currentMonthISO}
          height={140}
          isEmpty={isEmpty}
        />
      </div>

      {/* Phase 2 (2026-08-09) — "As of" timestamp so users know the
          figure is a live snapshot. Mirrors the Expenses (MTD) card. */}
      {asOf && (
        <p
          className="mt-3 text-[10px]"
          style={{ color: "var(--text-muted)" }}
          data-testid="income-hero-as-of"
        >
          As of {new Date(asOf).toLocaleString()}
        </p>
      )}
    </div>
  );
}

/** Phase 48 - YoY chip (green + up / red down / muted hidden). */
function YoyChip({ yoy }) {
  if (!yoy || !yoy.valid_for_display) {
    return (
      <span
        title="Insufficient prior-year data for a YoY comparison"
        className="inline-block rounded-full px-2 py-0.5 text-xs font-medium"
        style={{ backgroundColor: "var(--bg-hover)", color: "var(--text-muted)" }}
        data-testid="hero-yoy-hidden"
      >
        —
      </span>
    );
  }
  const pct = Number(yoy.pct || 0);
  const positive = pct >= 0;
  const arrow = positive ? "^" : "v";
  return (
    <span
      className="inline-block rounded-full px-2 py-0.5 text-xs font-medium"
      style={{
        backgroundColor: positive ? "var(--success-pale)" : "var(--danger-pale)",
        color: positive ? "var(--success)" : "var(--danger)",
      }}
      data-testid="hero-yoy-chip"
    >
      {`${arrow} ${positive ? "+" : ""}${pct.toFixed(1)}% YoY`}
    </span>
  );
}


function KpiCard({ label, value, icon: Icon, tint, to, testid, subtitle }) {
  const card = (
    <div
      className="rounded-xl bg-white border border-slate-200 p-4 flex items-center justify-between cursor-pointer hover:shadow-sm transition-all"
      data-testid={testid}
    >
      <div className="min-w-0">
        <p className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-medium">
          {label}
        </p>
        <p className="mt-1 text-[20px] font-semibold text-slate-900 tabular-nums truncate">
          {value}
        </p>
        {subtitle && (
          <p
            className="mt-0.5 text-[10px]"
            style={{ color: "var(--text-muted)" }}
            data-testid={`${testid}-subtitle`}
          >
            {subtitle}
          </p>
        )}
      </div>
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
        style={{ backgroundColor: tint.bg }}
      >
        <Icon className="h-4 w-4" style={{ color: tint.fg }} />
      </div>
    </div>
  );
  return to ? <Link to={to} className="block">{card}</Link> : card;
}

function AlertCard({ label, value, icon: Icon, accentColor, to, testid }) {
  const card = (
    <div
      className="rounded-xl bg-white border border-slate-200 p-4 flex items-center justify-between cursor-pointer hover:shadow-sm transition-all"
      style={{ borderLeft: `4px solid ${accentColor}` }}
      data-testid={testid}
    >
      <div className="min-w-0">
        <p className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-medium">
          {label}
        </p>
        <p className="mt-1 text-[20px] font-semibold text-slate-900 tabular-nums">{value}</p>
      </div>
      <Icon className="h-5 w-5 shrink-0" style={{ color: accentColor }} />
    </div>
  );
  return to ? <Link to={to} className="block">{card}</Link> : card;
}

/** Tooltip renderer for the Loans Overview bar chart — module-scoped so
 *  React doesn't rebuild the component tree on every LoansOverviewCard render. */
function LoansBarTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "2-digit" });
    } catch { return iso; }
  };
  return (
    <div
      className="rounded-md border p-2 text-xs shadow-md"
      style={{
        backgroundColor: "var(--bg-surface)",
        borderColor: "var(--border-default)",
        color: "var(--text-primary)",
      }}
    >
      <p className="font-medium mb-1">{p.label}</p>
      <p><span style={{ color: "var(--text-muted)" }}>Outstanding:</span>{" "}
         <span className="font-medium tabular-nums">{fmtMoney(p.outstanding)}</span></p>
      {p.next_payment_date && (
        <p className="mt-0.5"><span style={{ color: "var(--text-muted)" }}>Next payment:</span>{" "}
           <span className="tabular-nums">{fmtDate(p.next_payment_date)} · {fmtMoney(p.next_payment_amount)}</span></p>
      )}
    </div>
  );
}

/**
 * Phase 44c (2026-07-28) - Loans Overview widget (Vertical Bar Chart).
 * Replaces the earlier doughnut implementation. Every column is one active
 * loan tranche. Bar colors read from CSS theme tokens (--chart-color-1..5)
 * so it themes correctly in Dark / Eye Comfort / High Contrast.
 */
function LoansOverviewCard({ data }) {
  const navigate = useNavigate();
  const { mode, accent } = useAppearance();
  const loans = data?.loans || [];
  const CHART_VARS = ["--chart-color-1", "--chart-color-2", "--chart-color-3", "--chart-color-4", "--chart-color-5"];
  const FALLBACKS = ["#1F4FA3", "#0E9F6E", "#E63946", "#D97706", "#7C3AED"];
  const readVar = (v, idx) => {
    if (typeof document === "undefined") return FALLBACKS[idx];
    const raw = getComputedStyle(document.documentElement).getPropertyValue(v).trim();
    if (!raw || raw.includes(" ") || raw.startsWith("hsl")) return FALLBACKS[idx];
    return raw;
  };
  const palette = CHART_VARS.map((v, i) => readVar(v, i));

  // Derive short tranche labels for the x-axis (e.g. "LN-2026-001" -> "LN001",
  // "WHT-JE-01" -> "WHT01"). Falls back to first 6 chars of loan_id.
  const shortLabel = (l) => {
    const src = l.loan_number || l.tranche_label || l.loan_id || "";
    const parts = String(src).split(/[- ]/).filter(Boolean);
    if (parts.length === 0) return String(src).slice(0, 6);
    // Take leading letters + trailing numeric portion.
    const head = parts[0].replace(/[^A-Za-z]/g, "");
    const tail = parts[parts.length - 1].replace(/^0+/, "") || parts[parts.length - 1];
    return (head + tail).slice(0, 8) || String(src).slice(0, 6);
  };

  const barData = loans.map((l, i) => ({
    key: l.loan_id || i,
    short: shortLabel(l),
    outstanding: Number(l.outstanding || 0),
    label: l.tranche_label,
    color: palette[i % palette.length],
    next_payment_date: l.next_payment_date,
    next_payment_amount: l.next_payment_amount,
  }));

  const total = Number(data?.total_outstanding || 0);
  const nextPay = data?.aggregate_next_payment;
  const interestMtd = Number(data?.aggregate_interest_mtd || 0);
  const isEmpty = barData.length === 0 || total <= 0.005;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso + "T00:00:00").toLocaleDateString("en-US",
        { month: "short", day: "2-digit" });
    } catch { return iso; }
  };

  // Y-axis tick formatter: $Nk for readability.
  const fmtAxis = (v) => {
    const n = Number(v);
    if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (Math.abs(n) >= 1_000) return `$${Math.round(n / 1_000)}k`;
    return `$${n}`;
  };

  const CustomTooltip = LoansBarTooltip;

  // A `chartKey` that changes on theme swap forces recharts to re-render
  // with the fresh palette from CSS variables.
  const chartKey = `${mode}-${accent}-${barData.length}`;

  return (
    <div
      className="rounded-2xl bg-white border border-slate-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
      style={{ boxShadow: "var(--shadow-card)" }}
      onClick={() => navigate("/loans")}
      role="button"
      data-testid="chart-loans-overview"
    >
      {/* Header row */}
      <div className="flex items-start justify-between">
        <p className="text-[11px] tracking-[0.22em] uppercase font-medium" style={{ color: "var(--text-secondary)" }}>
          Outstanding Loans
        </p>
        <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>As of today</p>
      </div>

      {/* Headline strip: TOTAL OUTSTANDING label left, big number right */}
      <div className="mt-3 flex items-baseline justify-between" data-testid="loans-overview-headline">
        <p className="text-[10px] tracking-[0.22em] uppercase" style={{ color: "var(--text-muted)" }}>
          Total Outstanding
        </p>
        <p className="text-[28px] font-semibold tabular-nums leading-none" style={{ color: "var(--text-primary)" }}>
          {fmtMoney(total)}
        </p>
      </div>

      {/* Chart */}
      {isEmpty ? (
        <div
          className="mt-4 relative w-full flex flex-col items-center justify-center rounded-md border-2 border-dashed"
          style={{ height: 200, borderColor: "var(--border-default)" }}
          data-testid="loans-overview-empty"
        >
          <Landmark className="h-6 w-6 mb-2" style={{ color: "var(--text-muted)" }} />
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>No active loans</p>
        </div>
      ) : (
        <div className="mt-4" style={{ height: 200 }} data-testid="loans-overview-bar-chart">
          <ResponsiveContainer width="100%" height="100%" key={chartKey}>
            <BarChart data={barData} margin={{ top: 22, right: 8, bottom: 4, left: 4 }}
                      barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" vertical={false} />
              <XAxis
                dataKey="short"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: "var(--text-muted)" }}
              />
              <YAxis
                tickFormatter={fmtAxis}
                tickLine={false}
                axisLine={false}
                width={44}
                tick={{ fontSize: 11, fill: "var(--text-muted)" }}
              />
              <Tooltip
                cursor={{ fill: "var(--bg-hover)", opacity: 0.35 }}
                content={<CustomTooltip />}
              />
              <Bar dataKey="outstanding" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                {barData.map((d, i) => (
                  <Cell key={`c-${i}`} fill={d.color} />
                ))}
                <LabelList
                  dataKey="outstanding"
                  position="top"
                  formatter={(v) => fmtAxis(v)}
                  style={{ fontSize: 11, fill: "var(--text-primary)", fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Legend */}
      {!isEmpty && (
        <div className="mt-4" data-testid="loans-overview-legend">
          {barData.slice(0, 6).map((d, i) => (
            <div key={i} className="flex items-center justify-between text-sm py-1.5" style={{ borderTop: i === 0 ? "none" : "1px solid var(--border-default)" }}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="inline-block h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
                <span className="truncate" style={{ color: "var(--text-primary)" }}>
                  {d.label}
                </span>
              </div>
              <span className="tabular-nums font-medium shrink-0" style={{ color: "var(--text-primary)" }}>
                {fmtMoney(d.outstanding)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Bottom info strip */}
      <div
        className="mt-4 pt-3 flex items-center justify-between text-sm"
        style={{ borderTop: "1px solid var(--border-default)", color: "var(--text-secondary)" }}
        data-testid="loans-overview-info-strip"
      >
        <span>
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>Next payment:</span>{" "}
          {nextPay ? (
            <span className="font-medium" style={{ color: "var(--text-primary)" }}>
              {fmtDate(nextPay.date)} · {fmtMoney(nextPay.amount)}
            </span>
          ) : (
            <span className="text-xs">No payment due within 90 days</span>
          )}
        </span>
        <span>
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>Interest MTD:</span>{" "}
          <span className="font-medium tabular-nums" style={{ color: "var(--text-primary)" }}>
            {fmtMoney(interestMtd)}
          </span>
        </span>
      </div>
    </div>
  );
}

/* ------- decorative wave background ------- */
function WaveBackdrop() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute top-0 right-0 w-[560px] h-[380px] opacity-[0.06]"
      style={{ zIndex: 0 }}
    >
      <svg width="100%" height="100%" viewBox="0 0 560 380" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 210 C 100 160, 200 260, 320 200 S 520 150, 560 190" stroke="#1F4FA3" strokeWidth="1.5" fill="none" />
        <path d="M0 250 C 100 200, 200 300, 320 240 S 520 190, 560 230" stroke="#1F4FA3" strokeWidth="1.5" fill="none" />
        <path d="M0 290 C 100 240, 200 340, 320 280 S 520 230, 560 270" stroke="#1F4FA3" strokeWidth="1.5" fill="none" />
      </svg>
    </div>
  );
}

/* ------- main page ------- */

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [invStats, setInvStats] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [expenseMix, setExpenseMix] = useState(null);   // kept for legacy compat; unused visually
  const [loansOverview, setLoansOverview] = useState(null);
  const [trendCash, setTrendCash] = useState(null);
  const [trendIncome, setTrendIncome] = useState(null);
  const [trendExpenses, setTrendExpenses] = useState(null);
  const [txRows, setTxRows] = useState([]);
  const [aiQueue, setAiQueue] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [cashBreakdown, setCashBreakdown] = useState(null);
  const [cashFlowDays, setCashFlowDays] = useState(90);
  const [expensesRing, setExpensesRing] = useState(null);
  const [income12m, setIncome12m] = useState(null);
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  // Phase D (2026-10-04) — loan interest accrual widget
  const [accrualPending, setAccrualPending] = useState(null);
  const [accrualBusy, setAccrualBusy] = useState(false);

  const isDataEntry = user?.role === "somturk_data_entry";
  const isObserver = user?.role === "somturk_observer";

  const load = async (days = cashFlowDays) => {
    setRefreshing(true);
    const safe = async (path, setter) => {
      try {
        const { data } = await api.get(path);
        setter(data);
      } catch {
        /* endpoint may not exist for this role, ignore */
      }
    };
    await Promise.all([
      safe("/dashboard/stats", setStats),
      safe("/invoice-stats", setInvStats),
      safe(`/dashboard/cash-flow?days=${days}`, setCashFlow),
      safe("/dashboard/expense-mix?period=mtd", setExpenseMix),
      safe("/dashboard/loans-overview", setLoansOverview),
      safe("/dashboard/kpi-trend?metric=cash&days=30", setTrendCash),
      safe("/dashboard/kpi-trend?metric=income&days=30", setTrendIncome),
      safe("/dashboard/kpi-trend?metric=expenses&days=30", setTrendExpenses),
      safe("/dashboard/cash-position", setCashBreakdown),
      safe("/dashboard/expenses-ring", setExpensesRing),
      safe("/dashboard/income-12m", setIncome12m),
      safe("/loans/accrual-sweep/pending", setAccrualPending),
      (async () => {
        try {
          const { data } = await api.get("/transactions?status=posted,processed&limit=6");
          const rows = data?.transactions || data?.items || data || [];
          setTxRows(Array.isArray(rows) ? rows.slice(0, 6) : []);
        } catch { /* noop */ }
      })(),
      (async () => {
        if (isObserver) { setAiQueue([]); return; }
        try {
          const { data } = await api.get("/transactions?status=submitted&hasAIFields=true&limit=4");
          const rows = data?.transactions || data?.items || data || [];
          setAiQueue(Array.isArray(rows) ? rows.slice(0, 4) : []);
        } catch { /* noop */ }
      })(),
    ]);
    setLastUpdated(new Date());
    setRefreshing(false);
  };

  useEffect(() => {
    load(cashFlowDays);
    const id = setInterval(() => load(cashFlowDays), 30000);
    return () => clearInterval(id);
  }, []);

  // Phase 46 (2026-07-29) - Publish refresh action + timestamps to the shared
  // top bar so the compact clock/refresh cluster can drive them.
  const { register, clear } = useDashboardActions();
  useEffect(() => {
    register({
      refresh: () => load(cashFlowDays),
      refreshing,
      lastUpdated,
    });
  }, [refreshing, lastUpdated, cashFlowDays, register]);
  useEffect(() => () => clear(), [clear]);

  useEffect(() => {
    load(cashFlowDays);
  }, [cashFlowDays]);

  const welcome = buildWelcome(user, stats);

  // Delta computations for hero cards
  const heroDeltas = useMemo(() => {
    const computeDelta = (series) => {
      if (!series || series.length < 2) return 0;
      const mid = Math.floor(series.length / 2);
      const prev = series.slice(0, mid).reduce((a, p) => a + Number(p.value || 0), 0);
      const curr = series.slice(mid).reduce((a, p) => a + Number(p.value || 0), 0);
      if (Math.abs(prev) < 0.005) return curr === 0 ? 0 : (curr > 0 ? 5 : -5);
      return ((curr - prev) / Math.abs(prev)) * 100;
    };
    return {
      cash: computeDelta(trendCash?.series),
      income: computeDelta(trendIncome?.series),
      expenses: computeDelta(trendExpenses?.series),
    };
  }, [trendCash, trendIncome, trendExpenses]);

  // Cash Position — prefer server breakdown as single source of truth so the
  // KPI number and the popover subtotals always match to the cent. Fall back
  // to stats.cash+bank when breakdown hasn't loaded yet.
  const cashPosition = cashBreakdown?.total != null
    ? Number(cashBreakdown.total)
    : (Number(stats?.cash_balance || 0) + Number(stats?.bank_balance || 0));
  const cashSum = (cashBreakdown?.accounts || []).reduce((s, a) => s + Number(a.balance || 0), 0);
  const cashIntegrityDrift = cashBreakdown
    ? Math.abs(cashSum - Number(cashBreakdown.total || 0))
    : 0;
  // Per Phase 39c edge cases: hide MoM chip when either current or prior period
  // implies a negative/insufficient-history state. Auto-Heal engine (Phase 41)
  // replaces the amber warning banner — negative-cash is now announced via
  // notifications instead.
  const cashMomContext = {
    currentAbs: Math.abs(cashPosition),
    prevAbs: 999999,
    sameSign: cashPosition >= 0,
  };

  // Phase 45 (2026-07-28) - WoW % for the Cash Position card (last 7 vs prev 7).
  // Applies Phase 39c edge-case handling: hide if either window is negative,
  // insufficient magnitude, or NaN.
  const cashWoW = useMemo(() => {
    const series = trendCash?.series || [];
    if (series.length < 14) return { hide: true, deltaPct: 0 };
    const last7 = series.slice(-7).reduce((a, p) => a + Number(p.value || 0), 0);
    const prev7 = series.slice(-14, -7).reduce((a, p) => a + Number(p.value || 0), 0);
    if (last7 < 0 || prev7 < 0) return { hide: true, deltaPct: 0 };
    if (Math.abs(prev7) < 100) return { hide: true, deltaPct: 0 };
    const pct = ((last7 - prev7) / Math.abs(prev7)) * 100;
    if (!Number.isFinite(pct) || Math.abs(pct) >= 999) return { hide: true, deltaPct: 0 };
    return { hide: false, deltaPct: pct };
  }, [trendCash]);

  const heroCards = [
    {
      key: "hero-cash", label: "Cash Position", value: fmtMoney(cashPosition),
      deltaPct: heroDeltas.cash, series: trendCash?.series, sparkColor: BLUE,
      positive: heroDeltas.cash >= 0, to: "/bank",
      cashBreakdown: cashBreakdown,
      integrityDriftAbs: cashIntegrityDrift,
      momContext: cashMomContext,
      companion: cashBreakdown?.companion,
      // Phase 45 - WoW pill replaces the MoM chip.
      chipOverride: <WowChip hide={cashWoW.hide} deltaPct={cashWoW.deltaPct} />,
    },
    {
      key: "hero-income", label: "Total Income (MTD)", value: fmtMoney(stats?.total_income),
      deltaPct: heroDeltas.income, series: trendIncome?.series, sparkColor: GREEN,
      positive: heroDeltas.income >= 0, to: "/dashboard/income",
    },
    {
      key: "hero-expenses",
      label: "Total Expenses (YTD)",
      // Bug bundle #4 (2026-08-10) — Switched from MTD → YTD basis.
      // Users expected the hero card to tie to the "Total Expenses —
      // Breakdown" drill-down (which is YTD by default), not to the
      // month-only slice that hides ~11 months of the calendar year.
      // Fall back to the MTD number only if the server hasn't shipped
      // the new field yet (rolling deploys).
      value: fmtMoney(
        Number.isFinite(stats?.total_expenses_ytd)
          ? stats.total_expenses_ytd
          : stats?.total_expenses,
      ),
      subtitle: stats?.ytd_window
        ? `Year-to-date, ${(stats.ytd_window.from || "").slice(0, 4)}`
        : null,
      // YoY chip: this-year YTD-through-today vs last-year YTD-through-
      // same-calendar-day. `expenses_yoy_ytd_pct` is `null` when there
      // is no prior-year history — the guard on MomChip hides the pill.
      deltaPct: Number.isFinite(stats?.expenses_yoy_ytd_pct)
        ? stats.expenses_yoy_ytd_pct
        : null,
      series: trendExpenses?.series, sparkColor: "#94A3B8",
      positive: false, to: "/dashboard/expenses",
      asOf: stats?.as_of,
      momContext: {
        currentAbs: Math.abs(Number(stats?.total_expenses_ytd) || 0),
        prevAbs: Math.abs(Number(stats?.total_expenses_prev_year_ytd) || 0),
        sameSign: true,
      },
      // Phase 45 - Radial Progress Ring vs monthly budget replaces the sparkline.
      rightSlot: (
        <div className="flex flex-col items-center gap-2" data-testid="expenses-ring-slot">
          {expensesRing?.monthly_budget ? (
            <>
              <RadialProgressRing
                pct={Number(expensesRing?.pct_used || 0)}
                arcColorHint={expensesRing?.arc_color_hint || "success"}
                size={80}
                thickness={8}
                label="of budget"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setBudgetModalOpen(true);
                }}
                className="text-[10px] inline-flex items-center gap-1 hover:opacity-70"
                style={{ color: "var(--text-muted)" }}
                data-testid="expenses-ring-gear-btn"
                aria-label="Edit budget"
              >
                <SettingsIcon className="h-3 w-3" /> Edit
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setBudgetModalOpen(true);
              }}
              className="rounded-md border px-3 py-1.5 text-xs inline-flex items-center gap-1 hover:opacity-80"
              style={{
                borderColor: "var(--border-default)",
                color: "var(--text-secondary)",
                backgroundColor: "transparent",
              }}
              data-testid="expenses-set-budget-cta"
            >
              Set budget <span aria-hidden="true">+</span>
            </button>
          )}
        </div>
      ),
    },
  ];

  const secondaryKpis = [
    { key: "bank-balance", label: "Bank Balance", value: fmtMoney(stats?.bank_balance),
      icon: Landmark, tint: { bg: "#E7EEFB", fg: BLUE }, to: "/dashboard/bank-balance" },
    { key: "payables-due", label: "Payables Due", value: fmtMoney(stats?.payables_due),
      icon: ArrowUpRight, tint: { bg: "#FEECEE", fg: RED }, to: "/dashboard/payables" },
    { key: "receivables-due", label: "Receivables Due", value: fmtMoney(stats?.receivables_due),
      icon: ArrowDownLeft, tint: { bg: "#E6F5EE", fg: GREEN }, to: "/dashboard/receivables" },
    { key: "pending-payroll", label: "Pending Payroll", value: String(stats?.pending_payroll ?? 0),
      icon: Users, tint: { bg: "#FDF3E7", fg: AMBER }, to: "/dashboard/payroll-pending" },
    { key: "pending-recs", label: "Pending Reconciliations", value: String(stats?.pending_reconciliations ?? 0),
      icon: ClipboardCheck, tint: { bg: "#E7EEFB", fg: BLUE },
      to: "/bank?tab=reconciliation&status=draft&from=dashboard",
      hidden: isDataEntry || isObserver },
    { key: "missing-docs", label: "Missing Documents", value: String(stats?.missing_documents ?? 0),
      icon: FileText, tint: { bg: "#F1F5F9", fg: "#475569" },
      to: "/transactions?status=missing_document&from=dashboard" },
  ].filter((k) => !k.hidden);

  const alertCards = [
    { key: "alert-clarification", label: "Clarification Needed",
      value: String(stats?.clarification_needed ?? 0),
      icon: HelpCircle, accentColor: RED,
      to: "/transactions?status=clarification_needed&from=dashboard",
      hidden: isDataEntry || isObserver },
    { key: "alert-compliance", label: "Compliance Deadlines",
      value: String(stats?.compliance_deadlines ?? 0),
      icon: CalendarClock, accentColor: AMBER,
      to: "/compliance?filter=due_soon_or_overdue&from=dashboard" },
    { key: "alert-reports", label: "Reports Pending Approval",
      value: String(stats?.reports_pending_approval ?? 0),
      icon: AlertTriangle, accentColor: BLUE,
      to: "/approvals?status=submitted,reviewed&from=dashboard",
      hidden: isDataEntry || isObserver },
  ].filter((a) => !a.hidden);

  // Extended KPIs — Submitted, AI Extracted, Processed, Loans (keep drill-throughs)
  const extendedKpis = [
    { key: "submitted", label: "Submitted", value: String(stats?.submitted ?? 0),
      icon: Send, tint: { bg: "#E0F2FE", fg: "#0284C7" },
      to: "/transactions?status=submitted&from=dashboard",
      hidden: isObserver },
    { key: "ai-extracted", label: "AI Extracted Pending", value: String(stats?.ai_extracted_pending_review ?? 0),
      icon: Bot, tint: { bg: "#EDE9FE", fg: "#7C3AED" },
      to: "/transactions?status=submitted&hasAIFields=true&from=dashboard",
      hidden: isObserver },
    { key: "processed", label: "Processed", value: String(stats?.processed ?? 0),
      icon: CheckCheck, tint: { bg: "#E6F5EE", fg: GREEN },
      to: "/transactions?status=processed&from=dashboard" },
    { key: "loans-outstanding", label: "Outstanding Loans", value: fmtMoney(stats?.total_outstanding_loans),
      icon: Coins, tint: { bg: "#FEECEE", fg: RED },
      to: "/loans?status=active&from=dashboard" },
    { key: "loans-next-due", label: "Next Loan Payment",
      value: stats?.next_loan_payment_due_date
        ? `${stats.next_loan_payment_due_date}`
        : "—",
      icon: CalendarDays, tint: { bg: "#FDF3E7", fg: AMBER },
      to: "/loans?status=active&from=dashboard" },
    { key: "net-balance",
      // Bug bundle #4 (2026-08-10) — YTD basis to match the hero
      // Expenses card + drill-down. Formula:
      // total_income_ytd − total_expenses_ytd. Falls back to the
      // legacy `net_balance` field only if the server hasn't shipped
      // the YTD fields yet (rolling deploys).
      label: "Net Balance (YTD)",
      value: fmtMoney(
        Number.isFinite(stats?.net_balance_ytd)
          ? stats.net_balance_ytd
          : stats?.net_balance,
      ),
      subtitle: stats?.ytd_window
        ? `Year-to-date, ${(stats.ytd_window.from || "").slice(0, 4)}`
        : null,
      icon: Wallet, tint: { bg: "#E7EEFB", fg: NAVY },
      to: "/dashboard/net-balance" },
  ].filter((k) => !k.hidden);

  // Invoice mini-cards
  const invoiceMini = [
    { k: "customer_invoices_count", label: "Customer Invoices",
      to: "/invoices?type=customer&from=dashboard", money: false },
    { k: "supplier_invoices_count", label: "Supplier Invoices",
      to: "/invoices?type=supplier&from=dashboard", money: false },
    { k: "total_receivables", label: "Total Receivables",
      to: "/invoices?type=customer&from=dashboard", money: true },
    { k: "total_payables", label: "Total Payables",
      to: "/invoices?type=supplier&from=dashboard", money: true },
    { k: "overdue_customer_invoices", label: "Overdue Cust Inv.",
      to: "/invoices?type=customer&filter=overdue&from=dashboard", money: false },
    { k: "overdue_supplier_invoices", label: "Overdue Sup Inv.",
      to: "/invoices?type=supplier&filter=overdue&from=dashboard", money: false },
    { k: "paid_this_month", label: "Paid This Month",
      to: "/invoices?filter=paid_this_month&from=dashboard", money: true },
  ];

  return (
    <div className="relative space-y-6" data-testid="dashboard-page">
      <WaveBackdrop />
      <div className="relative" style={{ zIndex: 1 }}>

        {/* Phase 46 (2026-07-29) - Welcome ribbon removed. Date/live-clock/refresh
            moved into the shared top bar; approvals/compliance metadata already
            visible on the individual KPI cards. */}

        {/* HERO ROW */}
        <div className="grid gap-6 grid-cols-1 md:grid-cols-3" data-testid="hero-row">          {heroCards.map((h) => (
            h.key === "hero-cash" ? (
              <CashPositionHeroCard
                key={h.key}
                value={h.value}
                cashBreakdown={h.cashBreakdown}
                integrityDriftAbs={h.integrityDriftAbs}
                companion={h.companion}
                chipOverride={h.chipOverride}
              />
            ) : h.key === "hero-income" ? (
              <TotalIncomeHeroCard
                key={h.key}
                income12m={income12m}
                stats={stats}
              />
            ) : (
              <HeroCard
                key={h.key}
                label={h.label}
                value={h.value}
                deltaPct={h.deltaPct}
                momContext={h.momContext}
                series={h.series}
                sparkColor={h.sparkColor}
                positive={h.positive}
                to={h.to}
                testid={`hero-${h.key}`}
                cashBreakdown={h.cashBreakdown}
                integrityDriftAbs={h.integrityDriftAbs}
                companion={h.companion}
                rightSlot={h.rightSlot}
                chipOverride={h.chipOverride}
                asOf={h.asOf}
                subtitle={h.subtitle}
              />
            )
          ))}
        </div>

        {/* CHART ROW */}
        <div className="mt-6 grid gap-6 grid-cols-1 xl:grid-cols-3">
          {/* Cash Flow - CUMULATIVE NET AREA (Phase 45) - span 2/3 */}
          <div
            className="xl:col-span-2 rounded-2xl bg-white border border-slate-200 p-6"
            style={{ boxShadow: "0 1px 2px rgba(15,30,61,0.04), 0 10px 20px -16px rgba(15,30,61,0.15)" }}
            data-testid="chart-cash-flow"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[11px] tracking-[0.22em] uppercase font-medium"
                   style={{ color: "var(--text-secondary)" }}>
                  Cash Flow - Cumulative Net (Last {cashFlowDays} Days)
                </p>
                <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
                  Total cash gained or lost over the period
                </p>
              </div>
              <select
                value={cashFlowDays}
                onChange={(e) => setCashFlowDays(Number(e.target.value))}
                className="text-xs border rounded-md px-2 py-1"
                style={{
                  borderColor: "var(--border-default)",
                  backgroundColor: "var(--bg-surface)",
                  color: "var(--text-primary)",
                }}
                data-testid="cash-flow-range"
              >
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>
            </div>
            <CumulativeNetArea
              cumulativeSeries={cashFlow?.cumulative_series || []}
              finalValue={cashFlow?.cumulative_net_final ?? 0}
              height={260}
            />
          </div>

          {/* Phase 44 (2026-07-28) — Loans Overview (replaces Expense Mix) — 1/3 */}
          <LoansOverviewCard data={loansOverview} />
        </div>

        {/* Phase D (2026-10-04) — Loan Interest Accrual Status widget */}
        {accrualPending && (
          <div className="mt-6">
            <div
              className={
                "rounded-2xl p-4 border flex items-center justify-between gap-4 " +
                (accrualPending.count > 0
                  ? "bg-amber-50 border-amber-200"
                  : "bg-emerald-50 border-emerald-200")
              }
              data-testid="dashboard-interest-accrual-widget"
            >
              <div className="flex items-center gap-3">
                {accrualPending.count > 0
                  ? <AlertTriangle className="h-5 w-5 text-amber-700" />
                  : <CheckCheck className="h-5 w-5 text-emerald-700" />}
                <div>
                  <div className={accrualPending.count > 0 ? "text-amber-900 font-semibold" : "text-emerald-900 font-semibold"}>
                    {accrualPending.count > 0
                      ? `$${Number(accrualPending.total_interest).toFixed(2)} of loan interest pending`
                      : "All loan interest up to date"}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    {accrualPending.count > 0
                      ? `${accrualPending.count} overdue schedule row(s) across active loans. Automatic sweep runs 02:05 UTC daily.`
                      : "Daily sweep runs 02:05 UTC. Last check up to date."}
                  </div>
                </div>
              </div>
              {accrualPending.count > 0 && (
                <button
                  onClick={async () => {
                    setAccrualBusy(true);
                    try {
                      const { data } = await api.post("/loans/accrual-sweep/run");
                      toast.success(`${data.jes_created} interest accrual JE(s) posted ($${Number(data.total_interest).toFixed(2)}).`);
                      await load();
                    } catch (e) {
                      toast.error("Accrual sweep failed.");
                    } finally { setAccrualBusy(false); }
                  }}
                  disabled={accrualBusy}
                  className="inline-flex items-center gap-1 text-sm px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white rounded-md"
                  data-testid="dashboard-run-accrual-btn"
                >
                  {accrualBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
                  Run accrual now
                </button>
              )}
            </div>
          </div>
        )}

        {/* SECONDARY KPI GRID */}
        <div className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3" data-testid="secondary-kpi-grid">
          {secondaryKpis.map((k) => (
            <KpiCard
              key={k.key} label={k.label} value={k.value}
              icon={k.icon} tint={k.tint} to={k.to}
              testid={`kpi-${k.key}`}
            />
          ))}
        </div>

        {/* OPERATIONAL ALERTS */}
        {alertCards.length > 0 && (
          <div className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3" data-testid="alerts-row">
            {alertCards.map((a) => (
              <AlertCard
                key={a.key} label={a.label} value={a.value}
                icon={a.icon} accentColor={a.accentColor} to={a.to}
                testid={a.key}
              />
            ))}
          </div>
        )}

        {/* EXTENDED KPIs */}
        <div className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3" data-testid="extended-kpi-grid">
          {extendedKpis.map((k) => (
            <KpiCard
              key={k.key} label={k.label} value={k.value}
              icon={k.icon} tint={k.tint} to={k.to}
              subtitle={k.subtitle}
              testid={`kpi-${k.key}`}
            />
          ))}
        </div>

        {/* ACTIVITY SPLIT */}
        <div className="mt-6 grid gap-6 grid-cols-1 xl:grid-cols-3">
          {/* Recent Transactions */}
          <div
            className="xl:col-span-2 rounded-2xl bg-white border border-slate-200 p-6"
            data-testid="recent-transactions"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] tracking-[0.22em] text-slate-500 uppercase font-medium">
                Recent Transactions
              </p>
              <Link
                to="/transactions"
                className="inline-flex items-center gap-1 text-xs text-[#1F4FA3] font-medium hover:underline"
                data-testid="recent-tx-view-all"
              >
                View all <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] tracking-[0.18em] uppercase text-slate-500">
                    <th className="text-left font-medium py-2 pr-3">Date</th>
                    <th className="text-left font-medium py-2 pr-3">Description</th>
                    <th className="text-left font-medium py-2 pr-3">Entity</th>
                    <th className="text-right font-medium py-2 pr-3">Amount</th>
                    <th className="text-left font-medium py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(txRows || []).length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-slate-400 text-sm">
                        No recent transactions yet.
                      </td>
                    </tr>
                  ) : (
                    txRows.map((r) => {
                      const isNeg = ["supplier_invoice", "payment_out", "expense",
                        "operating_expense", "payroll", "payroll_disbursement",
                        "bank_charge", "tax_payment", "loan_repayment"].includes(r.transaction_type);
                      const amt = Number(r.amount || 0) * (isNeg ? -1 : 1);
                      const status = r.status || "posted";
                      const statusStyle = status === "posted" || status === "processed" || status === "reported"
                        ? "bg-emerald-50 text-emerald-700"
                        : status === "submitted"
                        ? "bg-amber-50 text-amber-700"
                        : "bg-slate-100 text-slate-600";
                      return (
                        <tr key={r.id} className="border-t border-slate-100">
                          <td className="py-2.5 pr-3 text-slate-600 whitespace-nowrap">{r.transaction_date}</td>
                          <td className="py-2.5 pr-3 text-slate-800 truncate max-w-[280px]">
                            {r.description || r.reference_number || r.transaction_type}
                          </td>
                          <td className="py-2.5 pr-3 text-slate-600 truncate max-w-[160px]">
                            {r.counterparty_name || "—"}
                          </td>
                          <td className={`py-2.5 pr-3 text-right font-medium tabular-nums ${amt >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                            {amt >= 0 ? "+" : "−"}{fmtMoney(Math.abs(amt))}
                          </td>
                          <td className="py-2.5">
                            <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] ${statusStyle}`}>
                              {status}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* AI Extraction Queue */}
          <div
            className="rounded-2xl bg-white border border-slate-200 p-6"
            data-testid="ai-queue"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] tracking-[0.22em] text-slate-500 uppercase font-medium">
                AI Extraction Queue
              </p>
              <Link
                to="/transactions?status=submitted&hasAIFields=true&from=dashboard"
                className="inline-flex items-center gap-1 text-xs text-[#1F4FA3] font-medium hover:underline"
                data-testid="ai-queue-view-all"
              >
                Review <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            {(aiQueue || []).length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center">
                No pending AI extractions.
              </div>
            ) : (
              <ul className="space-y-2">
                {aiQueue.map((r) => (
                  <li
                    key={r.id}
                    className="flex items-center gap-3 rounded-lg border border-slate-100 px-3 py-2 hover:bg-slate-50 transition-colors"
                    data-testid={`ai-queue-item-${r.id}`}
                  >
                    <div
                      className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                      style={{ backgroundColor: "#EDE9FE" }}
                    >
                      <Bot className="h-4 w-4" style={{ color: "#7C3AED" }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate">
                        {r.reference_number || r.description || r.transaction_type}
                      </p>
                      <p className="text-[11px] text-slate-500 truncate">
                        {r.counterparty_name || "—"} · {fmtMoney(r.amount)}
                      </p>
                    </div>
                    <Link
                      to={`/transactions/${r.id}`}
                      className="text-xs text-[#1F4FA3] font-medium hover:underline shrink-0"
                    >
                      Review →
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* INVOICE MINI-CARDS */}
        <div className="mt-6" data-testid="invoice-cards-grid">
          <div className="mb-3">
            <p className="text-[11px] tracking-[0.22em] text-slate-500 uppercase font-medium">
              Invoices
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Connected itemized invoices feeding AR / AP. Manual invoices are excluded.
            </p>
          </div>
          <div className="grid gap-4 grid-cols-2 xl:grid-cols-4">
            {invoiceMini.map((c) => {
              const v = invStats ? invStats[c.k] : 0;
              const txt = c.money ? fmtMoney(v || 0) : String(v ?? 0);
              const isNegative = c.k?.startsWith("overdue") && Number(v || 0) > 0;
              return (
                <Link
                  key={c.k}
                  to={c.to}
                  className="block rounded-xl bg-white border border-slate-200 p-4 hover:shadow-sm transition-all"
                  data-testid={`inv-card-${c.k}`}
                >
                  <div className="text-[11px] text-slate-500 uppercase tracking-[0.18em] font-medium">
                    {c.label}
                  </div>
                  <div className={`text-[20px] font-semibold mt-1 tabular-nums ${isNegative ? "text-red-600" : "text-slate-900"}`}>
                    {txt}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

      </div>

      {/* Phase 45 (2026-07-28) - Budget Settings Modal */}
      <BudgetSettingsModal
        isOpen={budgetModalOpen}
        onClose={() => setBudgetModalOpen(false)}
        onSaved={() => { load(cashFlowDays); }}
      />
    </div>
  );
}

